import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Tab,
  Tabs,
  Typography,
  Container,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  People as PeopleIcon,
  TrendingUp,
  Visibility,
  FileCopy,
  Close as CloseIcon,
  ListAlt,
  AccountBalance,
  Timeline,
} from '@mui/icons-material';
import { SharedContent } from '../types';
import { SharedContentService } from '../services/sharedContentService';
import SharedWatchlistView from '../components/SharedWatchlistView';
import SharedPortfolioView from '../components/SharedPortfolioView';
import PerformanceChartDialog from '../components/PerformanceChart';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`network-tabpanel-${index}`}
      aria-labelledby={`network-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Network: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sharedWatchlists, setSharedWatchlists] = useState<SharedContent[]>([]);
  const [sharedPortfolios, setSharedPortfolios] = useState<SharedContent[]>([]);
  const [portfolioData, setPortfolioData] = useState<Record<string, any>>({});
  const [selectedWatchlist, setSelectedWatchlist] = useState<SharedContent | null>(null);
  const [selectedPortfolio, setSelectedPortfolio] = useState<SharedContent | null>(null);
  const [watchlistDialogOpen, setWatchlistDialogOpen] = useState(false);
  const [portfolioDialogOpen, setPortfolioDialogOpen] = useState(false);
  const [performanceDialog, setPerformanceDialog] = useState<{
    open: boolean;
    portfolioId: string | null;
    portfolioName: string;
  }>({ open: false, portfolioId: null, portfolioName: '' });
  const navigate = useNavigate();

  useEffect(() => {
    loadSharedContent();
  }, []);

  const loadSharedContent = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Load shared watchlists
      const watchlists = await SharedContentService.getSharedContent('watchlist', 'recent', 20);
      setSharedWatchlists(watchlists);
      
      // Load shared portfolios
      const portfolios = await SharedContentService.getSharedContent('portfolio', 'popular', 10);
      setSharedPortfolios(portfolios);
      
      // Load portfolio data for metrics
      const portfolioDataMap: Record<string, any> = {};
      for (const portfolio of portfolios) {
        if (portfolio.portfolioId) {
          try {
            const { PortfolioService } = await import('../services/portfolioService');
            const data = await PortfolioService.getPortfolio(portfolio.portfolioId);
            if (data) {
              portfolioDataMap[portfolio.portfolioId] = data;
            }
          } catch (error) {
            console.error('Error loading portfolio data:', error);
          }
        }
      }
      setPortfolioData(portfolioDataMap);
    } catch (err: any) {
      console.error('Error loading shared content:', err);
      setError(err.message || 'Failed to load shared content');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleViewWatchlist = (watchlist: SharedContent) => {
    setSelectedWatchlist(watchlist);
    setWatchlistDialogOpen(true);
  };

  const handleViewPortfolio = (portfolio: SharedContent) => {
    setSelectedPortfolio(portfolio);
    setPortfolioDialogOpen(true);
  };

  const handleCloseWatchlistDialog = () => {
    setWatchlistDialogOpen(false);
    setSelectedWatchlist(null);
  };

  const handleClosePortfolioDialog = () => {
    setPortfolioDialogOpen(false);
    setSelectedPortfolio(null);
  };

  const handleCopyStock = (symbol: string) => {
    // Refresh the page or show a success message
    console.log(`Copied ${symbol} to watchlist`);
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Network
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Discover and follow traders, explore shared portfolios and watchlists
        </Typography>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Discover" />
          <Tab label="Following" />
        </Tabs>
      </Paper>

      <TabPanel value={tabValue} index={0}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <Grid container spacing={3}>
            {/* Shared Watchlists Section */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ListAlt sx={{ mr: 1 }} />
                    <Typography variant="h6">Shared Watchlists</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Browse watchlists shared by the community
                  </Typography>
                  
                  {sharedWatchlists.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">
                        No shared watchlists yet. Be the first to share yours!
                      </Typography>
                    </Box>
                  ) : (
                    <Grid container spacing={2}>
                      {sharedWatchlists.map((watchlist) => (
                        <Grid item xs={12} sm={6} md={4} key={watchlist.id}>
                          <Card variant="outlined" sx={{ cursor: 'pointer', '&:hover': { boxShadow: 2 } }}>
                            <CardContent onClick={() => handleViewWatchlist(watchlist)}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Avatar
                                  src={watchlist.ownerProfilePic}
                                  sx={{ width: 40, height: 40, mr: 2 }}
                                >
                                  {watchlist.ownerDisplayName.charAt(0).toUpperCase()}
                                </Avatar>
                                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                  <Typography variant="subtitle1" noWrap>
                                    {watchlist.ownerDisplayName}
                                  </Typography>
                                  {watchlist.ownerHandle && (
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                      @{watchlist.ownerHandle}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                              
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="caption" color="text.secondary">
                                  {formatDistanceToNow(watchlist.sharedAt, { addSuffix: true })}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Chip
                                    icon={<Visibility fontSize="small" />}
                                    label={watchlist.viewCount}
                                    size="small"
                                    variant="outlined"
                                  />
                                  <Chip
                                    icon={<FileCopy fontSize="small" />}
                                    label={watchlist.copyCount}
                                    size="small"
                                    variant="outlined"
                                  />
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Shared Portfolios Section */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AccountBalance sx={{ mr: 1 }} />
                    <Typography variant="h6">Shared Portfolios</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Browse portfolios shared by the community
                  </Typography>
                  
                  {sharedPortfolios.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">
                        No shared portfolios yet. Be the first to share yours!
                      </Typography>
                    </Box>
                  ) : (
                    <Grid container spacing={2}>
                      {sharedPortfolios.map((portfolio) => {
                        const pData = portfolio.portfolioId ? portfolioData[portfolio.portfolioId] : null;
                        const totalReturn = pData ? (pData.totalEarnings / pData.initialFundValue) * 100 : 0;

                        return (
                          <Grid item xs={12} sm={6} md={4} key={portfolio.id} sx={{ display: 'flex' }}>
                            <Card variant="outlined" sx={{ '&:hover': { boxShadow: 2 }, width: '100%', display: 'flex', flexDirection: 'column' }}>
                              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                {/* Portfolio Name - Prominent */}
                                <Typography
                                  variant="h6"
                                  sx={{
                                    mb: 1,
                                    cursor: 'pointer',
                                    '&:hover': { color: 'primary.main' }
                                  }}
                                  onClick={() => handleViewPortfolio(portfolio)}
                                >
                                  {pData?.name || 'Portfolio'}
                                </Typography>

                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                  <Avatar
                                    src={portfolio.ownerProfilePic}
                                    sx={{ width: 32, height: 32, mr: 1 }}
                                  >
                                    {portfolio.ownerDisplayName.charAt(0).toUpperCase()}
                                  </Avatar>
                                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                    <Typography variant="body2" noWrap>
                                      {portfolio.ownerDisplayName}
                                    </Typography>
                                    {portfolio.ownerHandle && (
                                      <Typography variant="caption" color="text.secondary" noWrap>
                                        @{portfolio.ownerHandle}
                                      </Typography>
                                    )}
                                  </Box>
                                  <Chip
                                    label={pData?.type || 'Portfolio'}
                                    size="small"
                                    color={pData?.type === 'real' ? 'success' : 'primary'}
                                  />
                                </Box>

                                {/* Performance Metrics */}
                                {pData && pData.sharedSettings?.showPerformance && (
                                  <Box sx={{ mb: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                                    <Grid container spacing={2}>
                                      <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">
                                          Total Earnings
                                        </Typography>
                                        <Typography variant="body2" color={pData.totalEarnings >= 0 ? 'success.main' : 'error.main'}>
                                          {pData.totalEarnings >= 0 ? '+' : ''}
                                          {new Intl.NumberFormat('en-US', {
                                            style: 'currency',
                                            currency: 'USD',
                                            minimumFractionDigits: 0,
                                            maximumFractionDigits: 0,
                                          }).format(pData.totalEarnings)}
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">
                                          Total Return
                                        </Typography>
                                        <Typography variant="body2" color={totalReturn >= 0 ? 'success.main' : 'error.main'}>
                                          {totalReturn >= 0 ? '+' : ''}{totalReturn.toFixed(1)}%
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                  </Box>
                                )}

                                {/* Spacer to push footer to bottom */}
                                <Box sx={{ flexGrow: 1 }} />

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Chip
                                      icon={<Visibility fontSize="small" />}
                                      label={portfolio.viewCount}
                                      size="small"
                                      variant="outlined"
                                    />
                                    <Chip
                                      icon={<FileCopy fontSize="small" />}
                                      label={portfolio.copyCount}
                                      size="small"
                                      variant="outlined"
                                    />
                                  </Box>
                                  <Button
                                    size="small"
                                    startIcon={<Timeline />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (portfolio.portfolioId) {
                                        setPerformanceDialog({
                                          open: true,
                                          portfolioId: portfolio.portfolioId,
                                          portfolioName: pData?.name || 'Portfolio'
                                        });
                                      }
                                    }}
                                    disabled={!portfolio.portfolioId}
                                  >
                                    Performance
                                  </Button>
                                </Box>

                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                  Shared {formatDistanceToNow(portfolio.sharedAt, { addSuffix: true })}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Following
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Traders and content you're following
                </Typography>
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">
                    You're not following anyone yet. Discover traders in the Discover tab!
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>
      
      {/* Shared Watchlist Dialog */}
      <Dialog
        open={watchlistDialogOpen}
        onClose={handleCloseWatchlistDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '60vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Shared Watchlist</Typography>
            <IconButton onClick={handleCloseWatchlistDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedWatchlist && (
            <SharedWatchlistView
              sharedContent={selectedWatchlist}
              onCopyStock={handleCopyStock}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Shared Portfolio Dialog */}
      <Dialog
        open={portfolioDialogOpen}
        onClose={handleClosePortfolioDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '60vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Shared Portfolio</Typography>
            <IconButton onClick={handleClosePortfolioDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedPortfolio && (
            <SharedPortfolioView
              sharedContentId={selectedPortfolio.id}
              onClose={handleClosePortfolioDialog}
              onImportStock={handleCopyStock}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Performance Chart Dialog */}
      <PerformanceChartDialog
        open={performanceDialog.open}
        onClose={() => setPerformanceDialog({ open: false, portfolioId: null, portfolioName: '' })}
        portfolioId={performanceDialog.portfolioId}
        portfolioName={performanceDialog.portfolioName}
      />
    </Container>
  );
};

export default Network;